/**
 * `?showcase=characters` — dev collision grid exercising the real module behaviour:
 * keyboard / tap movement with sliding collision, BFS click-to-move, party followers, two NPCs on
 * deterministic schedules (one with a rain behaviour), emotes on keys 1-4 (atlas icons) and 8 / 9 / 0
 * (content names `laugh` / `hammer` / `coin` resolved through EMOTE_ALIAS), weather toggle on R,
 * second follower on F, input lock on L, sit / write / idle poses on 5 / 6 / 7 (赵呈博 has no write
 * frames → declared fallback `sit`).
 */
import Phaser from 'phaser';
import { Depth, FONT_BODY, GAME_H, GAME_W, TILE } from '@/core/config';
import { bus, Events } from '@/core/events';
import { SeededRng } from '@/core/rng';
import type { NpcScheduleStep } from '@/core/types/contracts';
import type { WeatherState } from '@/core/types';
import { SHOWCASES } from '@/content/ShowcaseScene';
import { Actor } from './Actor';
import { ActorSystem } from './ActorSystem';
import { Follower } from './Follower';
import { NpcScheduleRunner } from './NpcScheduleRunner';
import { PlayerController } from './PlayerController';
import { ensureDevTileTextures } from './devTextures';
import { feetOverlap } from './feet';
import { GRID_H, GRID_W, buildShowcaseGrid } from './showcaseGrid';

export { buildShowcaseGrid } from './showcaseGrid';

function drawGridCanvas(scene: Phaser.Scene, grid: Uint8Array, w: number, h: number): string {
  const key = 'dev-showcase-grid';
  if (scene.textures.exists(key)) scene.textures.remove(key);
  const tiles = ensureDevTileTextures(scene);
  const floor = scene.textures.get(tiles.floor).getSourceImage() as HTMLCanvasElement;
  const wall = scene.textures.get(tiles.wall).getSourceImage() as HTMLCanvasElement;
  const canvas = document.createElement('canvas');
  canvas.width = w * TILE; canvas.height = h * TILE;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.imageSmoothingEnabled = false;
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) ctx.drawImage(grid[y * w + x] ? wall : floor, x * TILE, y * TILE);
    ctx.fillStyle = 'rgba(0,0,0,0.08)';
    for (let x = 0; x < w; x++) for (let y = 0; y < h; y++) if ((x + y) % 2) ctx.fillRect(x * TILE, y * TILE, TILE, TILE);
  }
  scene.textures.addCanvas(key, canvas);
  return key;
}

const tc = (tx: number, ty: number) => ({ x: tx * TILE + TILE / 2, y: ty * TILE + TILE / 2 });
/** HUD text for the emote an actor shows: `laugh→ui-emote-bubbles:note`, or —. */
const emoteLine = (a: Actor) => { const e = a.currentEmote; return e ? `${e.name}→${e.texture}${e.frame ? ':' + e.frame : ''}` : '—'; };

const MERCHANT_SCHEDULE: NpcScheduleStep[] = [
  { kind: 'walk', ...tc(22, 8) }, { kind: 'face', facing: 'down' }, { kind: 'wait', ms: 1500 },
  { kind: 'emote', emote: 'exclaim', ms: 900 },
  { kind: 'walk', ...tc(32, 8) }, { kind: 'face', facing: 'left' }, { kind: 'wait', ms: 1200 },
  { kind: 'walk', ...tc(32, 15) }, { kind: 'wait', ms: 800 },
  { kind: 'walk', ...tc(22, 15) }, { kind: 'face', facing: 'up' }, { kind: 'wait', ms: 1000 },
  { kind: 'loop' },
];
const MERCHANT_RAIN: NpcScheduleStep[] = [
  { kind: 'walk', ...tc(27, 8) }, { kind: 'face', facing: 'up' }, { kind: 'emote', emote: 'sweat', ms: 1200 },
  { kind: 'wait', ms: 2500 }, { kind: 'face', facing: 'left' }, { kind: 'wait', ms: 1500 }, { kind: 'face', facing: 'right' }, { kind: 'wait', ms: 1500 },
  { kind: 'loop' },
];
const LUO_SCHEDULE: NpcScheduleStep[] = [
  { kind: 'face', facing: 'left' }, { kind: 'wait', ms: 1800 },
  { kind: 'face', facing: 'right' }, { kind: 'wait', ms: 1200 },
  { kind: 'emote', emote: 'anger', ms: 1000 },
  { kind: 'walk', ...tc(10, 12) }, { kind: 'wait', ms: 900 }, { kind: 'walk', ...tc(8, 15) }, { kind: 'face', facing: 'down' }, { kind: 'wait', ms: 1400 },
  { kind: 'loop' },
];

interface ShowcaseHandle { player: Actor; followers: Follower[]; npcs: Record<string, Actor>; controller: PlayerController; runner: NpcScheduleRunner; system: ActorSystem; grid: Uint8Array; weather: WeatherState; locked: boolean }
declare global { interface Window { __nantianCharacters?: ShowcaseHandle } }

export function buildCharactersShowcase(scene: Phaser.Scene): void {
  const grid = buildShowcaseGrid();
  const bounds = { w: GRID_W * TILE, h: GRID_H * TILE };
  const isBlocked = (px: number, py: number) => {
    const tx = Math.floor(px / TILE), ty = Math.floor(py / TILE);
    if (tx < 0 || ty < 0 || tx >= GRID_W || ty >= GRID_H) return true;
    return grid[ty * GRID_W + tx] !== 0;
  };
  // NPCs block everyone else (party members never block: the follower trail already walks through the leader's path).
  const blockers: Actor[] = [];
  const isActorBlocked = (self: Actor, fx: number, fy: number) => blockers.some((n) => n !== self && feetOverlap(fx, fy, n.fx, n.fy));
  const opts = { isBlocked, bounds, isActorBlocked };
  scene.cameras.main.setBackgroundColor('#0b0f14');
  scene.add.image(0, 0, drawGridCanvas(scene, grid, GRID_W, GRID_H)).setOrigin(0).setDepth(Depth.GROUND);

  const p = tc(5, 5);
  const player = new Actor(scene, 'player', 'jiang-zichao', p.x, p.y, 'down', opts);
  const controller = new PlayerController(scene, player, { mapId: 'showcase' });
  const zhao = new Actor(scene, 'zhao', 'zhao-chengbo', p.x, p.y, 'down', opts);
  const followers: Follower[] = [new Follower(scene, zhao, player, { slot: 1 })];
  let qiong: Actor | null = null;

  const m = tc(22, 8), l = tc(12, 12);
  const merchant = new Actor(scene, 'merchant', 'yabei-merchant', m.x, m.y, 'down', opts);
  const luo = new Actor(scene, 'luo', 'luo-dahu', l.x, l.y, 'left', opts);
  blockers.push(merchant, luo);
  const ctxSeed = (scene.registry.get('ctx') as { seed?: number } | undefined)?.seed ?? 1157;
  const runner = new NpcScheduleRunner(scene, new SeededRng(ctxSeed), { initialWeather: 'sultry' });
  runner.add('merchant', merchant, MERCHANT_SCHEDULE, { rain: MERCHANT_RAIN, drizzle: MERCHANT_RAIN });
  runner.add('luo', luo, LUO_SCHEDULE);

  const cam = scene.cameras.main;
  cam.setBounds(0, 0, bounds.w, bounds.h);
  cam.startFollow(player.sprite, true, 1, 1);
  cam.roundPixels = true;

  // Live Chinese name labels (never baked into textures).
  const labelFor = (a: Actor) => scene.add.text(a.x, a.y - 50, a.displayName, { fontFamily: FONT_BODY, fontSize: '11px', color: '#fff4d6', stroke: '#1a1210', strokeThickness: 3, resolution: 2 }).setOrigin(0.5, 1);
  const labels = new Map<Actor, Phaser.GameObjects.Text>([[player, labelFor(player)], [zhao, labelFor(zhao)], [merchant, labelFor(merchant)], [luo, labelFor(luo)]]);

  const handle: ShowcaseHandle = { player, followers, npcs: { merchant, luo }, controller, runner, system: ActorSystem.get(scene), grid, weather: 'sultry', locked: false };
  window.__nantianCharacters = handle;

  const hud = scene.add.text(6, 6, '', { fontFamily: FONT_BODY, fontSize: '12px', color: '#e2c48c', backgroundColor: 'rgba(11,15,20,0.72)', padding: { x: 6, y: 4 }, resolution: 2, lineSpacing: 2 }).setScrollFactor(0).setDepth(Depth.UI);
  // Help sits directly under the HUD (top-left) so it never covers a name label at the bottom of the view.
  const help = scene.add.text(6, 6, '方向键 / WASD 移动 · 点击地面 走过去 · 1-4 表情图标 · 8/9/0 笑/敲/钱（别名）· 5/6/7 坐/写/站 · R 天气 · F 第二随从 · L 锁定输入', { fontFamily: FONT_BODY, fontSize: '11px', color: '#c9b89a', backgroundColor: 'rgba(11,15,20,0.72)', padding: { x: 6, y: 3 }, resolution: 2, wordWrap: { width: GAME_W - 24 } }).setOrigin(0, 0).setScrollFactor(0).setDepth(Depth.UI);

  let lastMoved = '—';
  const onMoved = (e: { x: number; y: number; facing: string }) => { lastMoved = `${e.x},${e.y} ${e.facing}`; };
  bus.onTyped(Events.PLAYER_MOVED, onMoved);

  const onUpdate = () => {
    // Name labels: an actor within 32 px of one whose label is shown (the party stacks 28 px apart) hides its own label;
    // insertion order decides (the player first).
    const shown: { x: number; y: number }[] = [];
    for (const [a, t] of labels) {
      const clash = shown.some((p) => Math.hypot(p.x - a.x, p.y - a.y) < 32);
      const lift = a.currentEmote ? 18 : 0; // the bubble sits 52 px above the feet: keep the name above it
      t.setVisible(!clash).setPosition(a.x, a.y - 50 - lift).setDepth(a.sprite.depth + 2);
      if (!clash) shown.push({ x: a.x, y: a.y });
    }
    const merchantProg = runner.get('merchant');
    hud.setText([
      `角色演示 · 天气 ${handle.weather} · ${handle.locked ? '输入已锁定' : '可操作'}`,
      `江子潮 (${player.x},${player.y}) ${player.facing} ${player.state}${player.isMoving() ? ' 移动中' : ''}`,
      `赵呈博 (${zhao.x},${zhao.y}) ${zhao.facing}${qiong ? `  琼 (${qiong.x},${qiong.y})` : ''}`,
      `亚北商贩 步骤 ${merchantProg?.currentIndex ?? 0} ${merchantProg?.currentPhase ?? ''} · 罗大湖 步骤 ${runner.get('luo')?.currentIndex ?? 0}`,
      `player:moved → ${lastMoved} · 固定步 ${handle.system.steps}`,
      `动画 ${player.currentAnimKey} · 赵 ${zhao.currentAnimKey}`,
      `表情 ${emoteLine(player)} · 商贩 ${emoteLine(merchant)} · 罗 ${emoteLine(luo)}`,
    ]);
    help.setPosition(6, hud.y + hud.height + 4);
  };
  scene.events.on(Phaser.Scenes.Events.UPDATE, onUpdate);

  const kb = scene.input.keyboard;
  const emotes = ['exclaim', 'question', 'note', 'dots'];
  const aliased = ['laugh', 'hammer', 'coin'];
  const keyHandlers: Record<string, () => void> = {
    'keydown-ONE': () => void player.showEmote(emotes[0] as string),
    'keydown-TWO': () => void player.showEmote(emotes[1] as string),
    'keydown-THREE': () => void player.showEmote(emotes[2] as string),
    'keydown-FOUR': () => void player.showEmote(emotes[3] as string),
    'keydown-EIGHT': () => void player.showEmote(aliased[0] as string, 2000),
    'keydown-NINE': () => void player.showEmote(aliased[1] as string, 2000),
    'keydown-ZERO': () => void player.showEmote(aliased[2] as string, 2000),
    'keydown-FIVE': () => { player.play('sit'); zhao.play('sit'); },
    'keydown-SIX': () => { player.play('write'); zhao.play('write'); },
    'keydown-SEVEN': () => { player.play('idle'); zhao.play('idle'); },
    'keydown-R': () => {
      handle.weather = handle.weather === 'rain' ? 'sultry' : 'rain';
      bus.emitTyped(Events.WEATHER_CHANGE, { state: handle.weather, t: 1 });
    },
    'keydown-F': () => {
      if (qiong) { const f = followers.pop(); f?.destroy(); labels.get(qiong)?.destroy(); labels.delete(qiong); qiong.destroy(); qiong = null; return; }
      qiong = new Actor(scene, 'qiong', 'qiong', player.x, player.y, player.facing, opts);
      followers.push(new Follower(scene, qiong, player, { slot: 2 }));
      labels.set(qiong, labelFor(qiong));
    },
    'keydown-L': () => { handle.locked = !handle.locked; controller.setInputLocked(handle.locked); runner.setPaused(handle.locked); },
  };
  for (const [ev, fn] of Object.entries(keyHandlers)) kb?.on(ev, fn);

  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
    for (const [ev, fn] of Object.entries(keyHandlers)) kb?.off(ev, fn);
    scene.events.off(Phaser.Scenes.Events.UPDATE, onUpdate);
    bus.offTyped(Events.PLAYER_MOVED, onMoved);
    for (const f of followers) f.destroy();
    controller.destroy();
    runner.destroy();
    for (const a of [player, zhao, merchant, luo, qiong]) a?.destroy();
    labels.clear();
    if (window.__nantianCharacters === handle) delete window.__nantianCharacters;
  });
}

export function registerShowcase(): void {
  SHOWCASES['characters'] = buildCharactersShowcase;
}
